import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as MoneyIcon,
  Verified as VerifiedIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import Currency from "../../common/Currency";
import { AdminUserDetail } from "../../../types/admin";
import {
  useUserSessions,
  useUserAddresses,
  useUserLoginHistory,
} from "../../../hooks/useAdminUsers";

interface UserDetailModalProps {
  open: boolean;
  user: AdminUserDetail | null;
  onClose: () => void;
  onEdit?: (user: AdminUserDetail) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  open,
  user,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);

  const { data: sessionsData } = useUserSessions(
    user?.id || "",
    activeTab === 1 && !!user?.id,
  );
  const { data: addressesData } = useUserAddresses(
    user?.id || "",
    activeTab === 2 && !!user?.id,
  );
  const { data: loginHistoryData } = useUserLoginHistory(
    user?.id || "",
    historyPage,
    10,
    activeTab === 3 && !!user?.id,
  );

  if (!user) return null;

  const sessions = sessionsData?.data || [];
  const addresses = addressesData?.data || [];
  const loginHistory = loginHistoryData?.data;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleClose = () => {
    setActiveTab(0);
    setHistoryPage(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar src={user.profilePicture} sx={{ width: 56, height: 56 }}>
            {user.firstName?.[0]}
            {user.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">{user.fullName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.users.userId")}: {user.id}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label={t("admin.users.details")} />
          <Tab label={t("admin.users.sessions")} />
          <Tab label={t("admin.users.addresses")} />
          <Tab label={t("admin.users.loginHistory")} />
        </Tabs>

        {/* Tab 0: Details */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("admin.users.basicInformation")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("admin.users.email")}
                    secondary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {user.email}
                        {user.isEmailVerified && (
                          <VerifiedIcon color="success" fontSize="small" />
                        )}
                      </Box>
                    }
                  />
                </ListItem>

                {user.phoneNumber && (
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("admin.users.phone")}
                      secondary={user.phoneNumber}
                    />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("admin.users.authType")}
                    secondary={
                      <Chip
                        label={user.authType}
                        size="small"
                        color={
                          user.authType === "google"
                            ? "primary"
                            : user.authType === "admin"
                              ? "secondary"
                              : "default"
                        }
                      />
                    }
                  />
                </ListItem>

                {(user.city || user.country) && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("admin.users.location")}
                      secondary={`${user.city || ""}${user.city && user.country ? ", " : ""}${user.country || ""}`}
                    />
                  </ListItem>
                )}
              </List>
            </Grid>

            {/* Account Status */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t("admin.users.accountStatus")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.users.status")}
                  </Typography>
                  <Chip
                    label={
                      user.isActive
                        ? t("admin.users.active")
                        : t("admin.users.inactive")
                    }
                    color={user.isActive ? "success" : "error"}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.users.emailVerification")}
                  </Typography>
                  <Chip
                    label={
                      user.isEmailVerified
                        ? t("admin.users.verified")
                        : t("admin.users.unverified")
                    }
                    color={user.isEmailVerified ? "success" : "warning"}
                    size="small"
                  />
                </Box>

                {/* Lockout Info */}
                {user.isLocked && (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "error.light",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="error.dark">
                      {t("admin.users.accountLocked")}
                      {user.lockoutEnd &&
                        ` - ${t("admin.users.lockoutUntil")}: ${formatDate(user.lockoutEnd)}`}
                    </Typography>
                    <Typography variant="caption" color="error.dark">
                      {t("admin.users.failedAttempts")}:{" "}
                      {user.failedLoginAttempts || 0}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.users.roles")}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {user.roles.map((role, index) => (
                      <Chip
                        key={index}
                        label={role}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>

                {/* Session Info */}
                {user.activeSessionCount !== undefined && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.activeSessions")}
                    </Typography>
                    <Typography variant="body1">
                      {user.activeSessionCount}
                    </Typography>
                  </Box>
                )}
                {user.lastLoginIp && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.lastLoginIp")}
                    </Typography>
                    <Typography variant="body1">
                      {user.lastLoginIp}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Activity & Statistics */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("admin.users.activityAndStatistics")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    textAlign="center"
                    p={2}
                    bgcolor="background.paper"
                    borderRadius={1}
                  >
                    <OrdersIcon color="primary" />
                    <Typography variant="h6">
                      {user.totalOrders || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.totalOrders")}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    textAlign="center"
                    p={2}
                    bgcolor="background.paper"
                    borderRadius={1}
                  >
                    <MoneyIcon color="success" />
                    <Typography variant="h6">
                      <Currency amount={user.totalSpent || 0} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.totalSpent")}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    textAlign="center"
                    p={2}
                    bgcolor="background.paper"
                    borderRadius={1}
                  >
                    <CalendarIcon color="info" />
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.created")}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(user.createdAt)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    textAlign="center"
                    p={2}
                    bgcolor="background.paper"
                    borderRadius={1}
                  >
                    <CalendarIcon color="info" />
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.users.lastLogin")}
                    </Typography>
                    <Typography variant="body2">
                      {user.lastLoginAt
                        ? formatDate(user.lastLoginAt)
                        : t("admin.users.never")}
                    </Typography>
                  </Box>
                </Grid>

                {user.lastOrderDate && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      textAlign="center"
                      p={2}
                      bgcolor="background.paper"
                      borderRadius={1}
                    >
                      <CalendarIcon color="warning" />
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.users.lastOrder")}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(user.lastOrderDate)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* User Preferences */}
            {user.preferences && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t("admin.users.preferences")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.users.language")}
                      </Typography>
                      <Typography variant="body1">
                        {user.preferences.language ||
                          t("admin.users.notSet")}
                      </Typography>
                    </Grid>

                    {user.preferences.currency && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">
                          {user.preferences.currency}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Tab 1: Sessions */}
        {activeTab === 1 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.users.ipAddress")}</TableCell>
                  <TableCell>{t("admin.users.createdAt")}</TableCell>
                  <TableCell>{t("admin.users.expiresAt")}</TableCell>
                  <TableCell>{t("admin.users.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sessions as any[]).map((session: any) => (
                  <TableRow key={session.id}>
                    <TableCell>{session.createdByIp}</TableCell>
                    <TableCell>
                      {new Date(session.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(session.expiresAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          session.isActive
                            ? t("admin.users.active")
                            : t("admin.users.revoked")
                        }
                        color={session.isActive ? "success" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {sessions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t("admin.users.noSessions")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 2: Addresses */}
        {activeTab === 2 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.users.address")}</TableCell>
                  <TableCell>{t("admin.users.city")}</TableCell>
                  <TableCell>{t("admin.users.country")}</TableCell>
                  <TableCell>{t("admin.users.phone")}</TableCell>
                  <TableCell>{t("admin.users.default")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(addresses as any[]).map((addr: any) => (
                  <TableRow key={addr.id}>
                    <TableCell>
                      {addr.addressLine1}
                      {addr.addressLine2 ? `, ${addr.addressLine2}` : ""}
                    </TableCell>
                    <TableCell>{addr.city}</TableCell>
                    <TableCell>{addr.country}</TableCell>
                    <TableCell>{addr.phoneNumber}</TableCell>
                    <TableCell>
                      {addr.isDefault && (
                        <Chip
                          size="small"
                          label={t("admin.users.default")}
                          color="primary"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {addresses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t("admin.users.noAddresses")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 3: Login History */}
        {activeTab === 3 && (
          <Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.users.date")}</TableCell>
                    <TableCell>{t("admin.users.ipAddress")}</TableCell>
                    <TableCell>{t("admin.users.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginHistory?.entries?.map((entry: any) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {new Date(entry.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{entry.ipAddress}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            entry.isActive
                              ? t("admin.users.active")
                              : entry.revokedAt
                                ? t("admin.users.revoked")
                                : t("admin.users.expired")
                          }
                          color={entry.isActive ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {loginHistory && loginHistory.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={loginHistory.totalPages}
                  page={historyPage}
                  onChange={(_, page) => setHistoryPage(page)}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t("common.close")}</Button>
        {onEdit && (
          <Button
            variant="contained"
            onClick={() => onEdit(user)}
            color="primary"
          >
            {t("admin.users.editUser")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailModal;
